import { useEffect, useState } from 'react';
import type { SpotifyAuthStatus } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { Alert, LanguageSwitch, Spinner } from '../components/common.tsx';
import { SpotifyBar } from '../components/SpotifyBar.tsx';
import type { RoomApi } from '../net/useRoom.ts';

const NAME_KEY = 'hitster.name';

/**
 * Startseite: hosten oder beitreten.
 *
 * Hosten setzt eine Spotify-Verknuepfung voraus (die Playlist muss ja
 * irgendwoher kommen). Beitreten geht ohne Konto -- wer nur mitspielt,
 * braucht kein Spotify.
 */
export function HomePage({
  api,
  authStatus,
  onAuthChanged,
}: {
  api: RoomApi;
  authStatus: SpotifyAuthStatus | null;
  onAuthChanged: () => void;
}) {
  const t = useT();
  const [mode, setMode] = useState<'choose' | 'host' | 'join'>('choose');
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [asSpectator, setAsSpectator] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Rueckmeldung vom OAuth-Redirect auswerten (?spotify=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('spotify');
    if (!result) return;

    if (result === 'denied') setNotice(t('spotify.denied'));
    else if (result === 'expired') setNotice(t('spotify.expired'));
    else if (result === 'failed') setNotice(t('spotify.failed'));
    else if (result === 'session_mismatch') setNotice(t('spotify.sessionMismatch'));
    else if (result === 'linked') setMode('host');

    // Query aufraeumen, damit ein Reload die Meldung nicht wiederholt.
    window.history.replaceState({}, '', window.location.pathname);
  }, [t]);

  // Beitritts-Link mit Code: /join/ABCD landet hier vorbefuellt.
  useEffect(() => {
    const match = /^\/join\/([A-Za-z0-9]{4})$/.exec(window.location.pathname);
    if (match?.[1]) {
      setCode(match[1].toUpperCase());
      setMode('join');
    }
  }, []);

  const remember = (value: string) => {
    setName(value);
    localStorage.setItem(NAME_KEY, value.trim());
  };

  const host = async () => {
    setBusy(true);
    await api.createRoom(name.trim());
    setBusy(false);
  };

  const join = async () => {
    setBusy(true);
    await api.joinRoom(code.trim().toUpperCase(), name.trim(), asSpectator);
    setBusy(false);
  };

  const nameValid = name.trim().length >= 1 && name.trim().length <= 20;
  const codeValid = /^[A-Za-z0-9]{4}$/.test(code.trim());

  return (
    <div className="page page--narrow">
      <div className="row row--between">
        <span className="text-xs text-dim">v0.1</span>
        <LanguageSwitch />
      </div>

      {/**
       * Der Titel traegt die Seite.
       *
       * Vorher: mittige Ueberschrift plus Untertitel in einem Kasten --
       * austauschbar. Jetzt linksbuendig und gross, mit dem zweiten Wort
       * in der Signaturfarbe. Der Blick hat einen Anfang.
       */}
      <header className="brand">
        <h1 className="brand__title">
          Hitster<span className="brand__accent">.</span>
        </h1>
        <p className="brand__tagline">{t('app.tagline')}</p>
      </header>

      {notice && <Alert kind="warning">{notice}</Alert>}
      {api.closedReason && <Alert kind="info">{t('error.roomClosed')}</Alert>}
      {!api.connected && <Alert kind="warning">{t('error.disconnected')}</Alert>}

      {/* ----- Auswahl ----- */}
      {mode === 'choose' && (
        <div className="stack">
          {/* Zwei grosse Flächen statt Knopf plus Kleingedrucktem —
              es sind die einzigen zwei Entscheidungen dieser Seite. */}
          <button
            type="button"
            className="choice choice--primary"
            onClick={() => setMode('host')}
          >
            <span className="choice__label">{t('home.hostGame')}</span>
            <span className="choice__hint">{t('home.hostGameHint')}</span>
          </button>

          <button type="button" className="choice" onClick={() => setMode('join')}>
            <span className="choice__label">{t('home.joinGame')}</span>
            <span className="choice__hint">{t('home.joinGameHint')}</span>
          </button>
        </div>
      )}

      {/* ----- Hosten ----- */}
      {mode === 'host' && (
        <div className="panel panel--solid stack">
          <SpotifyBar status={authStatus} onChanged={onAuthChanged} />

          {!authStatus?.linked && (
            <Alert kind="info">{t('home.spotifyRequired')}</Alert>
          )}

          <div className="field">
            <label className="field__label" htmlFor="host-name">
              {t('home.yourName')}
            </label>
            <input
              id="host-name"
              className="input"
              autoComplete="nickname"
              maxLength={20}
              placeholder={t('home.namePlaceholder')}
              value={name}
              onChange={(event) => remember(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && nameValid && authStatus?.linked) void host();
              }}
            />
          </div>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={!nameValid || !authStatus?.linked || busy}
            onClick={() => void host()}
          >
            {busy ? <Spinner /> : t('home.create')}
          </button>

          <button type="button" className="btn btn--ghost" onClick={() => setMode('choose')}>
            {t('common.back')}
          </button>
        </div>
      )}

      {/* ----- Beitreten ----- */}
      {mode === 'join' && (
        <div className="panel panel--solid stack">
          <div className="field">
            <label className="field__label" htmlFor="join-code">
              {t('home.roomCode')}
            </label>
            <input
              id="join-code"
              className="input input--code"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={4}
              placeholder="ABCD"
              value={code}
              onChange={(event) =>
                setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
              }
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="join-name">
              {t('home.yourName')}
            </label>
            <input
              id="join-name"
              className="input"
              autoComplete="nickname"
              maxLength={20}
              placeholder={t('home.namePlaceholder')}
              value={name}
              onChange={(event) => remember(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && nameValid && codeValid) void join();
              }}
            />
          </div>

          {/* Zuschauen ist der Weg fuer den Rechner am Fernseher: grosse
              Ansicht und Ton, ohne einen Spielplatz zu belegen. */}
          <div>
            <label className="switch">
              <input
                type="checkbox"
                checked={asSpectator}
                onChange={(event) => setAsSpectator(event.target.checked)}
              />
              <span>{t('spectator.join')}</span>
            </label>
            <span className="field__hint">{t('spectator.joinHint')}</span>
          </div>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={!nameValid || !codeValid || busy}
            onClick={() => void join()}
          >
            {busy ? <Spinner /> : asSpectator ? t('spectator.join') : t('home.join')}
          </button>

          <button type="button" className="btn btn--ghost" onClick={() => setMode('choose')}>
            {t('common.back')}
          </button>
        </div>
      )}
    </div>
  );
}
